<template>
    <div :class="{ 'table-responsive': responsive, 'table-sticky': sticky }" v-if="loaded" @scroll="handleDebouncedScroll">
        <table ref="appDatatable" class="table table-striped table-bordered" data-sticky-header="true">
            <thead>
                <tr>
                    <th
                        v-for="(column, col) in header"
                        :class="{
                            'column__sortable': column.sortable,
                            'column__resizable': column.resizable,
                            'centered' : column.center
                        }"
                        :data-key="column.key"
                        :style="setThStyle(column)"
                        v-if="column.show"
                    >
                        <div v-bind:style="setStyle(column)">
                            <slot :name="`th-${column.key}`">
                                {{ column.name }}
                            </slot>
                            <a
                                v-if="column.sortable"
                                @click.prevent="setOrder(column)"
                                class="button-sortable"
                                :class="{ 'active' : column.key in order }"
                            >
                                <i class="fa fa-sort pull-right" v-if="order[column.key] === undefined"></i>
                                <i class="fa fa-sort-asc pull-right" v-if="column.key in order && order[column.key] === 'asc'"></i>
                                <i class="fa fa-sort-desc pull-right" v-if="column.key in order && order[column.key] === 'desc'"></i>
                            </a>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody v-if="rowData.length > 0">
                <tr
                    v-for="(row, index) in rowData"
                    :key="`${index}-${row.id}`"
                    @click="$emit('clicked-row', row)"
                    :class="row.trClass"
                >
                    <td
                        v-for="(column, col) in columns"
                        v-if="column.show"
                        :class="{'centered' : column.center}"
                        :style="{ width: `${column.width}px` }"
                    >
                        <div>
                            <slot :name="column.key" v-bind="{ row, column, index }">
                                {{ processColumn(row, column) }}
                            </slot>
                        </div>
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td :colspan="columns.length" style="text-align:center">
                        <slot name="noResults">
                            Geen resultaten gevonden
                        </slot>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="pagination && totalCount !== null">
                <tr>
                    <td :colspan="columns.length">
                        <div class="row">
                          <div class="col-xs-3"></div>
                          <div class="col-xs-6 text-center">
                            <nav aria-label="pagination" v-if="pagination">
                              <ul class="pagination">
                                <li class="page-item" :class="{'disabled' : current_page < 2 }">
                                  <span v-if="current_page < 2" class="page-link">Vorige</span>
                                  <a v-else class="page-link" @click="page(current_page - 1)">Vorige</a>
                                </li>
                                <li class="page-item" v-if="current_page > 5">
                                  <a class="page-link" @click="page(1)">1</a>
                                </li>
                                <li class="page-item disabled" v-if="current_page > 6">
                                  <span class="page-link">...</span>
                                </li>
                                <li
                                    v-for="index in amountOfPages"
                                    :key="index"
                                    :class="{ 'active': index === current_page }">
                                    <template v-if="showPageLink(index)">
                                      <span v-if="index === current_page" class="page-link">{{ index }}</span>
                                      <a v-else class="page-link" @click="page(index)">{{ index }}</a>
                                    </template>
                                </li>
                                <li class="page-item disabled" v-if="current_page < (amountOfPages - 5)">
                                  <span class="page-link">...</span>
                                </li>
                                <li class="page-item" v-if="current_page < (amountOfPages - 5)">
                                  <a class="page-link" @click="page(amountOfPages)">{{ amountOfPages}}</a>
                                </li>

                                <li class="page-item" :class="{'disabled' : current_page === amountOfPages }">
                                  <span v-if="current_page === amountOfPages" class="page-link">Volgende</span>
                                  <a v-else class="page-link" @click="page(current_page + 1)">Volgende</a>
                                </li>
                              </ul>
                            </nav>
                          </div>
                          <div class="col-xs-3" v-if="totalCount !== null">
                            <div class="pull-right">
                              <strong v-text="rowData.length"></strong>
                              <span v-if="rowData.length === 1">resultaat</span>
                              <span v-else>resultaten</span>
                              /
                              <strong v-text="totalCount"></strong> chauffeurs
                            </div>
                          </div>
                        </div>

                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<script>
    export default {
        name: 'AppDatatable',

        data () {
            return {
              loaded: false,
              tableData: [],
              paginationData: null,
              order: {},
              pages: 1,
              current_page: 1,
              handleDebouncedScroll: null,
            }
        },

        props: {
            data: {
                type: [Array, Object],
                required: true
            },
            responsive: {
                type: Boolean,
                default: false
            },
            sticky: {
                type: Boolean,
                default: false
            },
            resizable: {
                type: Boolean,
                default: false
            },
            columns: {
                type: Array,
                required: true
            },
            multiOrder: {
                type: Boolean,
                default: false,
            },
            count: {
                type: Boolean,
                default: true,
            },
            totalCount: {
                type: Number,
                default: null,
            },
            pagination: {
              type: Boolean,
              default: true,
            },
            lazyLoad: {
              type: Boolean,
              default: false,
            },
            orderBy: {
                type: Object,
                default () {
                    return {
                        'name': 'asc'
                    }
                }
            },
            dragging: {
                type: Boolean,
                default: false
            }
        },
        mounted () {
            this.loaded = true
            this.order = this.orderBy
            this.handleDebouncedScroll = _.debounce(this.handleScroll, 100);

            setTimeout(() => {
                const columnsSelector = document.querySelectorAll('.column__resizable > div')
                const columns = Array.from(columnsSelector);
                columns.forEach((item) => {
                  let observer = new MutationObserver( (mutations) => {
                    if (this.dragging === false) {
                      const mutation = mutations[0];
                      const header = mutation.target.closest('.column__resizable');
                      if (header) {
                        const key = header.getAttribute('data-key')
                        this.columns.forEach((column, index) => {
                          if (column.key === key) {
                            this.$set(this.columns[index], 'width', mutation.target.offsetWidth);
                          }
                        })
                      } else {
                        console.warn(mutation);
                      }
                    }
                  });

                  observer.observe(item, {attributes: true});
                })
            }, 500)
        },

        computed: {
            amountOfPages() {
              const pages = parseInt(this.pages);
              if (isNaN(pages)) {
                return 1;
              }

              return pages;
            },

            header() {
                return this.columns;
            },

            rowData() {
                if (this.data.length === 0) {
                  return [];
                }

                if (this.data instanceof Object) {
                  this.tableData = this.data.data;
                  this.paginationData = this.data;
                  this.pages = parseInt(this.paginationData.last_page)
                } else {
                  this.tableData = this.data;
                }

                return this.tableData;
            },
        },

        methods: {
            processColumn(row, column) {
                if (row[column.key]) {
                    return row[column.key];
                }

                if (column.key.indexOf('.') > -1) {
                    let value = row;
                    const splitted = column.key.split('.');
                    splitted.forEach(key => {
                        try {
                          if (value[key]) {
                            value = value[key];
                          } else {
                            value = '';
                          }
                        } catch(e) {
                          value = '';
                        }
                    })

                    return value;
                }

                return '';
            },

            setThStyle(column) {
              const obj = {};
              if (column.width) {
                obj.width = column.width + 'px';
              }
              if (column.minWidth) {
                obj.minWidth = column.minWidth + 'px';
              }

              return obj;
            },

            setStyle(column) {
              const obj = {};
              if (column.width) {
                if (isNaN(column.width)) {
                  obj.width = column.width;
                } else {
                  obj.width = column.width + 'px';
                }
              }
              if (column.minWidth) {
                obj.minWidth = column.minWidth + 'px';
              }

              return obj;
            },

            setOrder(column) {
                if (column.key in this.order){
                    if (this.order[column.key] === 'asc') {
                        if (this.multiOrder === false) {
                            this.order = {}
                        }
                        this.$set(this.order, column.key, 'desc')
                    } else {
                        this.$delete(this.order, column.key)
                    }
                } else {
                    if (this.multiOrder === false) {
                        this.order = { }
                    }
                    this.$set(this.order, column.key, 'asc')
                }

                this.$emit('order', this.order);
            },

            page(page) {
              this.current_page = page;
              this.$emit('page', page);
            },

            showPageLink(page) {
              if (this.amountOfPages > 10) {
                if (this.current_page === page) {
                  return true
                } else if (this.current_page + 1 == page || this.current_page + 2 === page || this.current_page + 3 == page || this.current_page + 4 == page) {
                  return true;
                } else if (this.current_page - 1 == page || this.current_page - 2 === page || this.current_page - 3 == page || this.current_page - 4 == page) {
                  return true;
                }

                return false
              }

              return true;
            },

            handleScroll(event) {
                if (this.lazyLoad && this.loaded && this.data.next_page_url) {
                  const { scrollHeight, scrollTop, clientHeight } = event.target;
                  if (scrollTop + clientHeight > scrollHeight - 20) {
                      this.page(this.current_page + 1);
                  }
                }
            },
        }
    }
</script>



// WEBPACK FOOTER //
// resources/js/components/AppDatatable.vue